import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { observer } from "mobx-react-lite";
import { useServerDetails } from "../hooks/useServerDetails";
import { purchaseShopItem } from "../http/ShopAPI";
import { userConnect, userInfo } from "../http/userAPI";
import { getWebSocketInstance } from "../http/wsUserAPI";
import { getApiErrorMessage } from "../http/apiError";
import { Context } from "../index";
import BreadCrumbs from "../components/Breadcrumbs";
import ServerHero from "../components/serverDetails/ServerHero";
import ServerShop from "../components/serverDetails/ServerShop";
import ServerDescription from "../components/serverDetails/ServerDescription";
import ServerRating from "../components/serverDetails/ServerRating";
import ServerOnlineChart from "../components/serverDetails/ServerOnlineChart";
import PurchaseConfirmModal from "../components/serverDetails/PurchaseConfirmModal";
import AccountLinkModal from "../components/serverDetails/AccountLinkModal";
import TopUpModal from "../components/serverDetails/TopUpModal";
import PlayersOnlineModal from "../components/serverDetails/PlayersOnlineModal";
import ServerDetailsSnackbars from "../components/serverDetails/ServerDetailsSnackbars";

const ServerDetails = observer(() => {
    const { user } = useContext(Context);
    const navigate = useNavigate();
    const { server_id } = useParams();
    const isMobile = useMediaQuery("(max-width:600px)");

    const { server, setServer, loading: serverLoading, shopCategories } = useServerDetails(server_id, user.isAuth);
    const [steamName, setSteamName] = useState(null);
    const [usersInfo, setUsersInfo] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [isAuth, setIsAuth] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [connectSuccess, setConnectSuccess] = useState(false);
    const [connectFail, setConnectFail] = useState(false);
    const [canAccessChat, setCanAccessChat] = useState(false);
    const [hasAccountConnection, setHasAccountConnection] = useState(false);
    const [showRating, setShowRating] = useState(false);
    const [showShop, setShowShop] = useState(false);
    const [selectedTab, setSelectedTab] = useState("top_scores");
    const [topUpModalOpen, setTopUpModalOpen] = useState(false);
    const [insufficientFundsItem, setInsufficientFundsItem] = useState(null);
    const [playersModalOpen, setPlayersModalOpen] = useState(false);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [purchaseSnackbar, setPurchaseSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [confirmPurchaseModalOpen, setConfirmPurchaseModalOpen] = useState(false);
    const [itemToPurchase, setItemToPurchase] = useState(null);
    const [shopCategory, setShopCategory] = useState("all");

    useEffect(() => {
        if (user.isAuth) {
            setIsAuth(true);
            setUsersInfo(user.userInfo);
        } else {
            setIsAuth(false);
            setUsersInfo({});
        }
    }, [user.isAuth, user.userInfo]);

    useEffect(() => {
        setCanAccessChat(checkPermissions());
        setHasAccountConnection(isConnected());
    }, [usersInfo]);

    const filteredShopItems = useMemo(() => {
        if (!server || !Array.isArray(server.shop_items)) return [];
        if (shopCategory === "all") return server.shop_items;

        return server.shop_items.filter((item) => item.category_name === shopCategory);
    }, [server, shopCategory]);

    const checkPermissions = () => {
        const perms = usersInfo?.user_perms;
        if (!perms) return false;

        const perm = perms[Number(server_id)];
        return perm?.r === 1;
    };

    const isConnected = () => {
        if (!usersInfo?.user_connect || usersInfo.user_connect.length === 0) return false;

        const connect = usersInfo.user_connect.find((item) => item.server_id === Number(server_id));
        if (connect && connect.steam_name) setSteamName(connect.steam_name);

        return usersInfo.user_connect.some((connectItem) => connectItem.server_id === Number(server_id));
    };

    const handleCopyToClipboard = () => {
        if (!server) return;

        navigator.clipboard.writeText(`${server.ip}:${server.port}`);
        setCopySuccess(true);
    };

    const handleCloseSnackbar = () => {
        setCopySuccess(false);
        setConnectSuccess(false);
        setConnectFail(false);
    };

    const handleClosePurchaseSnackbar = () => {
        setPurchaseSnackbar((prev) => ({ ...prev, open: false }));
    };

    const handleToggleRating = () => {
        setShowRating((prev) => !prev);
        if (showShop) setShowShop(false);
    };

    const handleToggleShop = () => {
        setShowShop((prev) => !prev);
        if (showRating) setShowRating(false);
    };

    const handleChatClick = () => {
        if (canAccessChat) {
            navigate(`/chat/${server.id}`);
        } else {
            setModalOpen(true);
        }
    };

    const handleNeedAuth = () => {
        setPurchaseSnackbar({ open: true, message: "Пожалуйста, войдите в систему для покупки.", severity: "warning" });
    };

    const handleBuy = (item) => {
        setItemToPurchase(item);
        setConfirmPurchaseModalOpen(true);
    };

    const handleNeedTopUp = (item) => {
        setInsufficientFundsItem(item);
        setTopUpModalOpen(true);
    };

    const handleCloseConfirmPurchase = () => {
        setConfirmPurchaseModalOpen(false);
        setItemToPurchase(null);
    };

    const handleConfirmPurchase = async () => {
        if (!itemToPurchase || !server) return;

        setIsPurchasing(true);
        setConfirmPurchaseModalOpen(false);

        try {
            const purchaseResult = await purchaseShopItem(server.id, itemToPurchase.id);
            console.log("Результат покупки:", purchaseResult);

            if (purchaseResult && purchaseResult.status === true) {
                setPurchaseSnackbar({
                    open: true,
                    message: (
                        <>
                            {purchaseResult.message ||
                                `Покупка ${itemToPurchase.custom_name || itemToPurchase.name || `Предмет #${itemToPurchase.id}`} - ${itemToPurchase.stock} шт. прошла успешно!`}
                            <br />Заберите в игре, команда /shop
                        </>
                    ),
                    severity: "success",
                });
            } else {
                setPurchaseSnackbar({
                    open: true,
                    message: purchaseResult?.message || `Ошибка покупки ${itemToPurchase.custom_name || itemToPurchase.name || `Предмет #${itemToPurchase.id}`}`,
                    severity: "error",
                });
            }
        } catch (error) {
            console.error(getApiErrorMessage(error, "Ошибка сети или сервера при покупке"));
            setPurchaseSnackbar({
                open: true,
                message: "Произошла ошибка при попытке покупки. Пожалуйста, попробуйте позже.",
                severity: "error",
            });
        } finally {
            setIsPurchasing(false);
            setItemToPurchase(null);
        }
    };

    const refreshUserInfo = async () => {
        try {
            const data = await userInfo();
            console.log(data);
            user.setUserInfo(data);
            if (data.name) user.setUserName(data.name);
            return data;
        } catch (error) {
            console.error(getApiErrorMessage(error));
            return null;
        }
    };

    const handleConfirmLink = async () => {
        try {
            console.log(`Подтвердим данные ${server_id}`);
            const data = await userConnect(server_id, 1);
            console.log(data);

            if (data.status) {
                setConnectSuccess(true);
                setModalOpen(false);
                await refreshUserInfo();

                const webSocket = getWebSocketInstance(user);
                webSocket.reconnect(user);
            }
        } catch (error) {
            console.error("Failed to confirm account link:", error);
        }
    };

    const handleCancelLink = async () => {
        try {
            console.log(`Отменим подтверждение ${server_id}`);
            const data = await userConnect(server_id, 0);
            console.log(data);

            if (data.status) {
                setConnectFail(true);
                setModalOpen(false);
                await refreshUserInfo();
            }
        } catch (error) {
            console.error("Failed to cancel account link:", error);
        }
    };

    const handleGoToWallet = () => {
        navigate("/wallet");
        setTopUpModalOpen(false);
    };

    if (serverLoading || !server) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ width: "100%", marginTop: 3 }}>
                <BreadCrumbs hrefs="" name={server.name} />
            </Box>

            <Box sx={{ width: "100%", marginTop: 3 }}>
                <Paper sx={{ padding: 3, mb: 2 }}>
                    <ServerHero
                        server={server}
                        isMobile={isMobile}
                        isAuth={isAuth}
                        canAccessChat={canAccessChat}
                        showRating={showRating}
                        showShop={showShop}
                        onCopy={handleCopyToClipboard}
                        onOpenPlayers={() => setPlayersModalOpen(true)}
                        onToggleRating={handleToggleRating}
                        onToggleShop={handleToggleShop}
                        onChatClick={handleChatClick}
                    />

                    {showShop && server.shop_items && server.shop_items.length > 0 && (
                        <ServerShop
                            items={filteredShopItems}
                            categories={shopCategories}
                            selectedCategory={shopCategory}
                            onCategoryChange={setShopCategory}
                            usersInfo={usersInfo}
                            isAuth={isAuth}
                            isPurchasing={isPurchasing}
                            onNeedAuth={handleNeedAuth}
                            onBuy={handleBuy}
                            onNeedTopUp={handleNeedTopUp}
                        />
                    )}

                    <Box sx={{ mt: 2 }}>
                        {!showRating && !showShop && <ServerDescription description={server.description} />}

                        {showRating && server.xdStats && Object.keys(server.xdStats).length > 0 && (
                            <ServerRating stats={server.xdStats} selectedTab={selectedTab} onTabChange={setSelectedTab} />
                        )}
                    </Box>

                    <ServerOnlineChart data={server.player_online} />
                </Paper>
            </Box>

            <PurchaseConfirmModal
                open={confirmPurchaseModalOpen}
                item={itemToPurchase}
                onClose={handleCloseConfirmPurchase}
                onConfirm={handleConfirmPurchase}
            />

            <AccountLinkModal
                open={modalOpen}
                usersInfo={usersInfo}
                steamName={steamName}
                hasAccountConnection={hasAccountConnection}
                onConfirm={handleConfirmLink}
                onCancel={handleCancelLink}
                onRefresh={refreshUserInfo}
                onClose={() => setModalOpen(false)}
            />

            <TopUpModal
                open={topUpModalOpen}
                item={insufficientFundsItem}
                usersInfo={usersInfo}
                onClose={() => setTopUpModalOpen(false)}
                onGoToWallet={handleGoToWallet}
            />

            <PlayersOnlineModal
                open={playersModalOpen}
                players={server.online_players || []}
                onClose={() => setPlayersModalOpen(false)}
            />

            <ServerDetailsSnackbars
                connectSuccess={connectSuccess}
                connectFail={connectFail}
                copySuccess={copySuccess}
                purchaseSnackbar={purchaseSnackbar}
                onCloseMain={handleCloseSnackbar}
                onClosePurchase={handleClosePurchaseSnackbar}
            />
        </Container>
    );
});

export default ServerDetails;
